import { chatStore, clearChatConversations, resetChatState, setChatIdentity } from './chatStore';
import { businessCodesOfCustomToken, decodeJwtPayload, sameBusinessCodes } from './chatIdentity';
import { onChatAuthChanged, resumeChatAuth, signOutChat, startChatAuth } from './chatFirestore';

const CHAT_OWNER_KEY = 'baboonChatOwner';
const CHAT_OFF_KEY = 'baboonChatOff';
const CHAT_OFF_EPOCH = 1;
const NO_CHAT_USER_TYPES = [3, 5];

const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 60000;
const RETRIABLE_AUTH_CODES = [
	'auth/network-request-failed',
	'auth/internal-error',
	'auth/too-many-requests',
	'auth/timeout',
];

let retryTimer = null;
let authWatched = false;
let sessionOwner = null;

const ownerOf = (user) => (user && user.id != null && user.id !== '' ? String(user.id) : null);

const offMarkerOf = (owner) => `${owner}:${CHAT_OFF_EPOCH}`;

const isChatOff = (owner) => localStorage.getItem(CHAT_OFF_KEY) === offMarkerOf(owner);

const isCurrent = (teardowns) => teardowns === chatStore.teardowns;

const isRetriable = (error) => !error?.code || RETRIABLE_AUTH_CODES.includes(error.code);

export const retryDelay = (failures) => Math.min(RETRY_BASE_MS * 2 ** failures, RETRY_MAX_MS);

const clearRetry = () => {
	clearTimeout(retryTimer);
	retryTimer = null;
};

const scheduleRetry = (run, failures) => {
	clearRetry();
	retryTimer = setTimeout(() => {
		retryTimer = null;
		run();
	}, retryDelay(failures));
};

const adoptSignedInUser = () => {
	if (!chatStore.needsLogin || !sessionOwner || localStorage.getItem(CHAT_OWNER_KEY) !== sessionOwner) {
		return;
	}
	const teardowns = chatStore.teardowns;
	resumeChatAuth().then(
		(businessCodes) => {
			if (businessCodes && isCurrent(teardowns) && chatStore.needsLogin) {
				setChatIdentity(businessCodes);
			}
		},
		() => undefined
	);
};

const onStorage = (event) => {
	if (event.key === CHAT_OWNER_KEY) {
		adoptSignedInUser();
	}
};

const onAuthLost = () => {
	clearRetry();
	clearChatConversations();
	if (chatStore.available) {
		promptLogin();
	}
};

const watchAuth = () => {
	if (authWatched) {
		return;
	}
	authWatched = true;
	onChatAuthChanged(onAuthLost, adoptSignedInUser).then(
		() => window.addEventListener('storage', onStorage),
		() => {
			authWatched = false;
		}
	);
};

const promptLogin = () => {
	chatStore.connecting = false;
	chatStore.needsLogin = true;
	watchAuth();
};

const tokenExpiresWithin = (fToken, ms) => {
	const exp = decodeJwtPayload(fToken)?.exp;
	return typeof exp === 'number' && exp * 1000 <= Date.now() + ms;
};

const signIn = (fToken, owner, teardowns, failures) =>
	startChatAuth(fToken).then(
		(businessCodes) => {
			if (isCurrent(teardowns)) {
				localStorage.setItem(CHAT_OWNER_KEY, owner);
				setChatIdentity(businessCodes);
				watchAuth();
			}
		},
		(error) => {
			if (!isCurrent(teardowns)) {
				return;
			}
			if (isRetriable(error) && !tokenExpiresWithin(fToken, retryDelay(failures))) {
				scheduleRetry(() => signIn(fToken, owner, teardowns, failures + 1), failures);
			} else {
				promptLogin();
			}
		}
	);

const resume = (teardowns, failures) =>
	resumeChatAuth().then(
		(businessCodes) => {
			if (!isCurrent(teardowns)) {
				return;
			}
			if (businessCodes) {
				setChatIdentity(businessCodes);
				watchAuth();
			} else {
				promptLogin();
			}
		},
		(error) => {
			if (!isCurrent(teardowns)) {
				return;
			}
			if (isRetriable(error)) {
				scheduleRetry(() => resume(teardowns, failures + 1), failures);
			} else {
				promptLogin();
			}
		}
	);

export const startChatSession = (fToken, user, teardowns = chatStore.teardowns) => {
	if (!isCurrent(teardowns)) {
		return Promise.resolve();
	}
	clearRetry();
	const owner = ownerOf(user);
	const businessCodes = owner ? businessCodesOfCustomToken(fToken) : null;
	localStorage.removeItem(CHAT_OWNER_KEY);
	if (!businessCodes) {
		sessionOwner = null;
		if (owner) {
			localStorage.setItem(CHAT_OFF_KEY, offMarkerOf(owner));
		}
		resetChatState();
		chatStore.pendingOpen = null;
		return signOutChat();
	}
	sessionOwner = owner;
	localStorage.removeItem(CHAT_OFF_KEY);
	if (!sameBusinessCodes(businessCodes, chatStore.businessCodes)) {
		clearChatConversations();
	}
	chatStore.available = true;
	chatStore.needsLogin = false;
	chatStore.connecting = !chatStore.businessCodes;
	return signIn(fToken, owner, teardowns, 0);
};

export const resumeChatSession = (store) => {
	const { token, user } = store.getters;
	const owner = ownerOf(user);
	if (!token || !owner || chatStore.available || isChatOff(owner) || NO_CHAT_USER_TYPES.includes(user.type)) {
		return Promise.resolve();
	}
	sessionOwner = owner;
	const teardowns = chatStore.teardowns;
	chatStore.available = true;
	chatStore.connecting = true;
	chatStore.needsLogin = false;
	if (localStorage.getItem(CHAT_OWNER_KEY) !== owner) {
		return signOutChat().then(() => {
			if (isCurrent(teardowns)) {
				promptLogin();
			}
		});
	}
	return resume(teardowns, 0);
};

export const reviveChatSession = (store) => {
	const owner = ownerOf(store.getters.user);
	if (!owner || !isChatOff(owner)) {
		return Promise.resolve();
	}
	localStorage.removeItem(CHAT_OFF_KEY);
	return resumeChatSession(store);
};

export const tearDownChatSession = () => {
	chatStore.teardowns += 1;
	sessionOwner = null;
	clearRetry();
	resetChatState();
	localStorage.removeItem(CHAT_OWNER_KEY);
	localStorage.removeItem(CHAT_OFF_KEY);
	return signOutChat();
};
